import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { selectCurrentLanguage } from "features/language/languageSlice";

import { refreshToken, getAccessToken } from "components/utilities/sedra3/auth";

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL,

  prepareHeaders: (headers, { getState }) => {
    const token = getAccessToken();
    const state = getState();
    const currentLanguage = selectCurrentLanguage(state);

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    if (currentLanguage) {
      headers.set("X-Accept-Language", currentLanguage);
    }
    return headers;
  },
});

// Backend 403 details that mean "token is broken — try to refresh".
// Anything else with a 403 status is an authorisation miss (RBAC, scope)
// where refreshing the token will not help — we surface those to the
// caller and skip the refresh+logout pipeline so the user stays logged in.
//
// Source of these strings:
//   * backend/app/api/deps.py (get_current_user)  "Invalid token: …"
//                                                 "Token validation failed."
//   * backend/app/main.py:64                      "Invalid token"
const TOKEN_REFRESH_TRIGGERS = ["Token validation failed", "Invalid token"];

const isTokenRelated403 = (errorPayload) => {
  const detail = errorPayload?.data?.detail;
  if (typeof detail !== "string") return false;
  return TOKEN_REFRESH_TRIGGERS.some((needle) => detail.includes(needle));
};

export const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // Only run the refresh+logout pipeline on token-related 403s. Authorisation
  // misses (insufficient permissions, tenant violation that wasn't 404-coerced
  // by the backend) must not log the user out — those errors propagate to the
  // calling component which can show a toast or guide the user.
  if (
    result.error &&
    result.error.status === 403 &&
    isTokenRelated403(result.error)
  ) {
    try {
      const newToken = await refreshToken();
      // Retry the original query with the new token
      if (newToken) {
        const retryArgs = {
          ...args,
          headers: {
            ...(args.headers || {}),
            Authorization: `Bearer ${newToken}`,
          },
        };

        result = await baseQuery(retryArgs, api, extraOptions);
      }
    } catch (error) {
      console.error("Token refresh failed", error);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("language");
      // Also drop the JWT mirror cookie (Grafana SSO / SQLAdmin
      // auto-login). If we don't, nginx would still try to verify
      // against a token whose redis entry was wiped by the refresh
      // failure, get 401, and the user would land in a redirect loop.
      document.cookie = "access_token=; path=/; max-age=0";
    }
  }

  return result;
};

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    loginUser: builder.mutation({
      query: (credentials) => ({
        url: "/login",
        method: "POST",
        body: credentials,
      }),
    }),
    getBanks: builder.query({
      query: () => "/bank/list",
    }),
    getRolesByBank: builder.query({
      query: (bankId) => ({
        url: `/role/${bankId}/roles`,
        method: "GET",
        headers: {
          // Add any custom headers here if needed
        },
      }),
    }),
    registerUser: builder.mutation({
      query: (userDetails) => ({
        url: "/user",
        method: "POST",
        body: userDetails,
      }),
    }),
    resetPassword: builder.mutation({
      query: (passwordResetData) => ({
        url: "/login/reset-password",
        method: "POST",
        body: passwordResetData,
      }),
    }),
    passwordResetRequest: builder.mutation({
      query: (email) => ({
        url: "/login/password-reset-request",
        method: "POST",
        body: { email },
      }),
    }),
    changePassword: builder.mutation({
      query: (passwordChangeData) => ({
        url: "/login/change_password",
        method: "POST",
        body: passwordChangeData,
      }),
    }),
    logoutUser: builder.mutation({
      query: () => ({
        url: "/login/logout",
        method: "POST",
      }),
    }),
  }),
});

export const {
  useLoginUserMutation,
  useRegisterUserMutation,
  useGetBanksQuery,
  useGetRolesByBankQuery,
  useResetPasswordMutation,
  usePasswordResetRequestMutation,
  useChangePasswordMutation,
  useLogoutUserMutation,
} = authApi;
